import React, { useEffect } from "react";

/* ============================================================
 *  ARCANE PRO — LEGAL PAGES
 *  Páginas: Termos de Uso e Política de Privacidade
 *  Estilo: copia o visual do Landing (fundo escuro, Space Grotesk,
 *  glass strong, accent roxo Arcane #6366f1).
 *
 *  USO no App.js:
 *    import { TermosPage, PrivacidadePage } from './LegalPages';
 *    if (page === "termos") return <TermosPage onBack={() => setPage("landing")} />;
 *    if (page === "privacidade") return <PrivacidadePage onBack={() => setPage("landing")} />;
 *
 *  ROTA por hash URL (já implementado neste arquivo): se a URL tiver
 *  #/termos ou #/privacidade, abre direto. Útil pra link no rodapé.
 * ============================================================ */

const LEGAL_CSS = `
.legal-page { background: #050508; min-height: 100vh; color: #fff; font-family: 'Inter', system-ui, sans-serif; padding: 0; }
.legal-page::before {
  content: ''; position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background:
    radial-gradient(ellipse 60% 40% at 15% 0%, rgba(99,102,241,0.10), transparent 60%),
    radial-gradient(ellipse 60% 40% at 85% 100%, rgba(99,102,241,0.06), transparent 60%);
}
.legal-header {
  position: sticky; top: 0; z-index: 50;
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 32px;
  background: rgba(5,5,8,0.7);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255,255,255,0.05);
}
.legal-back {
  display: inline-flex; align-items: center; gap: 8px;
  background: transparent; border: 1px solid rgba(255,255,255,0.1);
  color: rgba(255,255,255,0.7); font-size: 12px; font-weight: 500;
  letter-spacing: 0.05em; padding: 8px 16px; border-radius: 10px;
  cursor: pointer; transition: all 0.2s;
  font-family: inherit;
}
.legal-back:hover { background: rgba(255,255,255,0.05); color: #fff; }
.legal-brand {
  display: inline-flex; align-items: center; gap: 8px;
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 14px; font-weight: 500; letter-spacing: 0.02em;
}
.legal-brand-dot {
  width: 10px; height: 10px; border-radius: 3px; background: #6366f1;
  box-shadow: 0 0 10px rgba(99,102,241,0.6);
}
.legal-content {
  position: relative; z-index: 1;
  max-width: 760px; margin: 0 auto; padding: 60px 24px 120px;
}
.legal-eyebrow {
  font-size: 11px; font-weight: 600; color: rgba(255,255,255,0.4);
  letter-spacing: 0.25em; text-transform: uppercase; margin-bottom: 16px;
}
.legal-title {
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: clamp(38px, 5vw, 56px); line-height: 1.05;
  letter-spacing: -0.03em; font-weight: 300;
  margin: 0 0 16px;
}
.legal-updated {
  font-size: 13px; color: rgba(255,255,255,0.4);
  margin-bottom: 48px;
  padding-bottom: 24px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.legal-content h2 {
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 24px; font-weight: 500; letter-spacing: -0.01em;
  margin: 56px 0 16px;
  color: #fff;
}
.legal-content h3 {
  font-size: 17px; font-weight: 600;
  margin: 32px 0 12px;
  color: rgba(255,255,255,0.95);
}
.legal-content p {
  font-size: 15px; line-height: 1.75; color: rgba(255,255,255,0.7);
  margin: 0 0 16px;
}
.legal-content strong { color: rgba(255,255,255,0.95); font-weight: 600; }
.legal-content ul, .legal-content ol {
  font-size: 15px; line-height: 1.75; color: rgba(255,255,255,0.7);
  margin: 0 0 16px; padding-left: 24px;
}
.legal-content li { margin-bottom: 8px; }
.legal-content a {
  color: #a5b4fc; text-decoration: none;
  border-bottom: 1px solid rgba(165,180,252,0.3);
  transition: all 0.15s;
}
.legal-content a:hover { color: #c7d2fe; border-color: rgba(199,210,254,0.6); }
.legal-content code {
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  background: rgba(99,102,241,0.08);
  border: 1px solid rgba(99,102,241,0.15);
  color: #a5b4fc; padding: 2px 8px; border-radius: 5px;
}
.legal-callout {
  border: 1px solid rgba(245,158,11,0.2);
  background: rgba(245,158,11,0.04);
  border-radius: 12px; padding: 16px 20px;
  margin: 20px 0;
  font-size: 14px; line-height: 1.7;
  color: rgba(252,211,77,0.95);
}
.legal-callout::before { content: '⚠️  '; }
.legal-table-wrap { overflow-x: auto; margin: 20px 0; }
.legal-table {
  width: 100%; border-collapse: collapse;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px; overflow: hidden;
  font-size: 13px;
}
.legal-table th {
  background: rgba(99,102,241,0.06);
  color: rgba(255,255,255,0.85);
  font-weight: 600; text-align: left;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  letter-spacing: 0.02em;
}
.legal-table td {
  padding: 12px 16px;
  color: rgba(255,255,255,0.65);
  border-bottom: 1px solid rgba(255,255,255,0.04);
  vertical-align: top;
}
.legal-table tr:last-child td { border-bottom: none; }
.legal-footer {
  margin-top: 80px; padding-top: 40px;
  border-top: 1px solid rgba(255,255,255,0.06);
  text-align: center; font-size: 12px; color: rgba(255,255,255,0.35);
  letter-spacing: 0.05em;
}
.legal-footer a { color: rgba(255,255,255,0.6); }
`;

// Header reutilizado
function LegalHeader({ onBack }) {
  return (
    <header className="legal-header">
      <button className="legal-back" onClick={onBack}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="m15 18-6-6 6-6" />
        </svg>
        Voltar
      </button>
      <div className="legal-brand">
        <span className="legal-brand-dot" />
        <span style={{ color: "#fff" }}>Arcane Pro</span>
      </div>
      <div style={{ width: 80 }} />
    </header>
  );
}

function LegalFooter() {
  return (
    <div className="legal-footer">
      <p>
        Arcane Pro · <a href="mailto:contato@arcanepro.com.br">contato@arcanepro.com.br</a>
      </p>
    </div>
  );
}

function useScrollTopOnMount() {
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);
}

// ============================================================
// TERMOS DE USO
// ============================================================
export function TermosPage({ onBack }) {
  useScrollTopOnMount();
  return (
    <div className="legal-page">
      <style>{LEGAL_CSS}</style>
      <LegalHeader onBack={onBack} />

      <main className="legal-content">
        <div className="legal-eyebrow">Documento Legal</div>
        <h1 className="legal-title">Termos de Uso</h1>
        <p className="legal-updated">Última atualização: 26 de abril de 2026</p>

        <p>
          Bem-vindo ao Arcane Pro. Estes Termos de Uso ("Termos") regulam o acesso e a utilização da plataforma Arcane Pro, disponível em <a href="https://arcanepro.com.br">arcanepro.com.br</a> ("Plataforma").
        </p>
        <p>
          Ao se cadastrar, acessar ou utilizar a Plataforma, você ("Usuário") declara que leu, entendeu e concorda integralmente com estes Termos. Se não concordar com qualquer cláusula, não utilize a Plataforma.
        </p>

        <h2>1. Definições</h2>
        <ul>
          <li><strong>Operador:</strong> Gabriel Polizel, pessoa física, responsável pela operação da Plataforma, podendo no futuro ser substituído por pessoa jurídica controlada pelo mesmo, hipótese em que estes Termos permanecem válidos com a sucessão automática.</li>
          <li><strong>Plataforma / Serviço:</strong> o software Arcane Pro, acessível via web, que utiliza inteligência artificial para analisar documentos enviados pelo Usuário e gerar diagnósticos textuais.</li>
          <li><strong>Conta:</strong> o cadastro do Usuário, identificado por e-mail e senha.</li>
          <li><strong>Conteúdo do Usuário:</strong> arquivos, textos e dados enviados pelo Usuário à Plataforma para análise.</li>
          <li><strong>Resultado:</strong> o texto, diagnóstico ou material gerado pela Plataforma a partir do Conteúdo do Usuário.</li>
        </ul>

        <h2>2. Natureza do Serviço</h2>

        <h3>2.1. O que é</h3>
        <p>A Plataforma é uma <strong>ferramenta de auxílio à análise</strong> que processa documentos enviados pelo Usuário (planilhas, contratos, textos) por meio de tecnologia de inteligência artificial e devolve um diagnóstico textual.</p>

        <h3>2.2. O que NÃO é</h3>
        <p>A Plataforma <strong>não constitui</strong>, em hipótese alguma:</p>
        <ul>
          <li>Consultoria contábil, fiscal, financeira, jurídica, de recursos humanos, de marketing ou de qualquer outra natureza profissional regulada;</li>
          <li>Aconselhamento profissional individualizado;</li>
          <li>Substituição de profissional habilitado (contador, advogado, consultor, etc.);</li>
          <li>Garantia de resultado ou exatidão das análises geradas.</li>
        </ul>

        <h3>2.3. Uso responsável</h3>
        <p>O Usuário <strong>reconhece e aceita</strong> que:</p>
        <ul>
          <li>Os Resultados são gerados por inteligência artificial e podem conter erros, imprecisões ou interpretações incorretas;</li>
          <li>Toda decisão tomada com base nos Resultados é de <strong>responsabilidade exclusiva</strong> do Usuário;</li>
          <li>O Operador <strong>recomenda fortemente</strong> que o Usuário valide os Resultados com profissional habilitado antes de tomar decisões relevantes.</li>
        </ul>

        <h2>3. Cadastro e Conta</h2>

        <h3>3.1. Quem pode usar</h3>
        <p>Para utilizar a Plataforma, o Usuário deve:</p>
        <ul>
          <li>Ser maior de 18 anos ou emancipado nos termos da lei brasileira;</li>
          <li>Possuir capacidade civil plena;</li>
          <li>Fornecer informações verdadeiras, completas e atualizadas no cadastro.</li>
        </ul>

        <h3>3.2. Segurança da conta</h3>
        <p>O Usuário é o único responsável por:</p>
        <ul>
          <li>Manter a confidencialidade da senha;</li>
          <li>Toda atividade realizada em sua Conta;</li>
          <li>Notificar imediatamente o Operador em caso de uso não autorizado.</li>
        </ul>

        <h3>3.3. Suspensão e encerramento</h3>
        <p>O Operador pode suspender ou encerrar a Conta, com ou sem aviso prévio, em caso de:</p>
        <ul>
          <li>Violação destes Termos;</li>
          <li>Uso fraudulento, abusivo ou ilícito;</li>
          <li>Determinação legal ou judicial;</li>
          <li>Inadimplência por mais de 7 dias.</li>
        </ul>

        <h2>4. Planos, Pagamentos e Cancelamento</h2>

        <h3>4.1. Planos</h3>
        <p>A Plataforma oferece os seguintes planos:</p>
        <ul>
          <li><strong>Starter (gratuito):</strong> até 20 análises por mês, com módulos limitados;</li>
          <li><strong>Essencial:</strong> R$ 97,00/mês, até 80 análises;</li>
          <li><strong>Profissional:</strong> R$ 247,00/mês, até 250 análises;</li>
          <li><strong>Gestão:</strong> R$ 497,00/mês, até 700 análises.</li>
        </ul>
        <p>Os limites mensais e os valores podem ser alterados a qualquer momento, mediante aviso prévio de 30 dias aos assinantes ativos.</p>

        <h3>4.2. Forma de pagamento</h3>
        <p>Os pagamentos são processados por gateways terceiros (atualmente Kirvano) e podem ser realizados via PIX, cartão de crédito ou boleto. O Operador <strong>não armazena dados de cartão de crédito</strong> em seus servidores.</p>

        <h3>4.3. Renovação automática</h3>
        <p>As assinaturas são renovadas automaticamente a cada ciclo (mensal), até que o Usuário solicite o cancelamento.</p>

        <h3>4.4. Cancelamento</h3>
        <p>O Usuário pode cancelar sua assinatura a qualquer momento, sem multa, por meio do painel do gateway de pagamento ou enviando solicitação para <a href="mailto:contato@arcanepro.com.br">contato@arcanepro.com.br</a>. O cancelamento será efetivado ao final do ciclo já pago — não há reembolso proporcional para períodos já iniciados.</p>

        <h3>4.5. Direito de arrependimento</h3>
        <p>Conforme o art. 49 do Código de Defesa do Consumidor, o Usuário tem <strong>7 (sete) dias corridos</strong> a partir da contratação para solicitar reembolso integral, contanto que ainda não tenha utilizado mais de 50% das análises do plano contratado.</p>

        <h3>4.6. Atraso e inadimplência</h3>
        <p>Em caso de atraso no pagamento, a Conta poderá ser suspensa após 3 dias e cancelada após 7 dias, com perda do acesso aos Resultados anteriormente gerados.</p>

        <h2>5. Uso Aceitável</h2>

        <h3>5.1. O Usuário se compromete a:</h3>
        <ul>
          <li>Utilizar a Plataforma apenas para finalidades lícitas;</li>
          <li>Não enviar conteúdo de terceiros sem autorização (contratos, planilhas alheias);</li>
          <li>Não enviar dados pessoais sensíveis de terceiros sem base legal;</li>
          <li>Não tentar burlar limites técnicos do plano contratado;</li>
          <li>Não realizar engenharia reversa, scraping ou cópia da Plataforma.</li>
        </ul>

        <h3>5.2. O Usuário NÃO pode usar a Plataforma para:</h3>
        <ul>
          <li>Atividades ilegais, fraudulentas ou que infrinjam direitos de terceiros;</li>
          <li>Geração de conteúdo difamatório, discriminatório ou ofensivo;</li>
          <li>Análise de documentos obtidos de forma ilícita;</li>
          <li>Revenda do Serviço sem autorização expressa do Operador.</li>
        </ul>

        <h3>5.3. Consequências</h3>
        <p>Violações resultam em <strong>suspensão imediata</strong> da Conta, sem reembolso, e podem ser comunicadas às autoridades competentes.</p>

        <h2>6. Conteúdo, Resultado e Propriedade Intelectual</h2>

        <h3>6.1. Sobre o Conteúdo do Usuário</h3>
        <ul>
          <li>O Usuário <strong>mantém a titularidade</strong> de todo Conteúdo enviado;</li>
          <li>O Usuário <strong>garante</strong> ter direitos sobre o Conteúdo enviado;</li>
          <li>O Usuário <strong>autoriza</strong> o Operador a processar, armazenar e analisar o Conteúdo, exclusivamente para a prestação do Serviço.</li>
        </ul>

        <h3>6.2. Sobre os Resultados</h3>
        <ul>
          <li>Os Resultados gerados pela Plataforma podem ser utilizados livremente pelo Usuário em seu negócio;</li>
          <li>O Operador <strong>não reivindica</strong> direitos sobre os Resultados gerados especificamente para o Usuário.</li>
        </ul>

        <h3>6.3. Sobre a Plataforma</h3>
        <ul>
          <li>A Plataforma, incluindo código, design, marca e identidade visual ("Arcane", "Arcane Pro"), é de propriedade exclusiva do Operador;</li>
          <li>O Usuário recebe <strong>licença limitada, não exclusiva e não transferível</strong> de uso da Plataforma enquanto a assinatura estiver ativa;</li>
          <li>É vedada qualquer cópia, modificação ou distribuição da Plataforma.</li>
        </ul>

        <h2>7. Limitação de Responsabilidade</h2>

        <h3>7.1. Inteligência Artificial e imprecisão</h3>
        <p>O Usuário <strong>expressamente reconhece</strong> que a Plataforma utiliza modelos de linguagem de IA, e que tais modelos podem:</p>
        <ul>
          <li>Gerar respostas incorretas, incompletas ou tendenciosas;</li>
          <li>Fabricar informações ("alucinar") em casos raros;</li>
          <li>Não capturar nuances específicas do contexto do Usuário.</li>
        </ul>

        <h3>7.2. Responsabilidade do Operador</h3>
        <p>Na medida máxima permitida pela legislação brasileira, o Operador <strong>não será responsável</strong> por:</p>
        <ul>
          <li>Decisões de negócio tomadas pelo Usuário com base nos Resultados;</li>
          <li>Lucros cessantes, perdas indiretas ou consequenciais;</li>
          <li>Indisponibilidades temporárias da Plataforma por motivo de força maior, manutenção ou problemas em serviços terceiros (gateway, hospedagem, provedores de IA);</li>
          <li>Falhas do Usuário em validar os Resultados com profissional habilitado.</li>
        </ul>

        <h3>7.3. Limite financeiro</h3>
        <p>A responsabilidade total e cumulativa do Operador, em qualquer hipótese, <strong>fica limitada ao valor pago pelo Usuário nos 3 (três) meses anteriores ao evento que originou a controvérsia</strong>, ou a R$ 100,00 (cem reais), o que for maior.</p>

        <h3>7.4. Não exclusão</h3>
        <p>Esta limitação não exclui responsabilidades que não possam ser limitadas por lei (como dolo, fraude ou violações reconhecidas pelo CDC).</p>

        <h2>8. Dados Pessoais e Privacidade</h2>
        <p>O tratamento de dados pessoais pelo Operador é regido pela <a href="#/privacidade">Política de Privacidade</a>, que faz parte integrante destes Termos. Ao aceitar estes Termos, o Usuário também aceita a Política de Privacidade.</p>

        <h2>9. Modificações dos Termos</h2>
        <p>O Operador pode modificar estes Termos a qualquer momento. Modificações <strong>substanciais</strong> serão comunicadas com <strong>30 dias de antecedência</strong> por e-mail e/ou aviso na Plataforma. O uso continuado após a vigência das modificações implica aceitação tácita.</p>

        <h2>10. Disposições Gerais</h2>

        <h3>10.1. Sucessão</h3>
        <p>Caso o Operador venha a constituir pessoa jurídica para exploração da Plataforma, todos os direitos e obrigações destes Termos serão automaticamente transferidos para essa pessoa jurídica, sem necessidade de novo aceite, hipótese em que o Usuário será comunicado por e-mail.</p>

        <h3>10.2. Independência das cláusulas</h3>
        <p>A invalidade de qualquer cláusula não afeta as demais.</p>

        <h3>10.3. Tolerância</h3>
        <p>A não exigência imediata de qualquer direito não significa renúncia.</p>

        <h3>10.4. Comunicações</h3>
        <p>Todas as comunicações oficiais devem ser feitas por escrito, via e-mail para <a href="mailto:contato@arcanepro.com.br">contato@arcanepro.com.br</a>.</p>

        <h2>11. Lei Aplicável e Foro</h2>
        <p>Estes Termos são regidos pela legislação brasileira.</p>
        <p>Fica eleito o foro da comarca de <strong>São Paulo/SP</strong> para dirimir quaisquer controvérsias, com renúncia a qualquer outro, por mais privilegiado que seja, ressalvadas as hipóteses em que o Código de Defesa do Consumidor determinar foro diverso.</p>

        <h2>12. Contato</h2>
        <p>Dúvidas sobre estes Termos podem ser enviadas para:</p>
        <p>
          <strong>E-mail:</strong> <a href="mailto:contato@arcanepro.com.br">contato@arcanepro.com.br</a><br />
          <strong>Site:</strong> <a href="https://arcanepro.com.br">arcanepro.com.br</a>
        </p>

        <LegalFooter />
      </main>
    </div>
  );
}

// ============================================================
// POLÍTICA DE PRIVACIDADE
// ============================================================
export function PrivacidadePage({ onBack }) {
  useScrollTopOnMount();
  return (
    <div className="legal-page">
      <style>{LEGAL_CSS}</style>
      <LegalHeader onBack={onBack} />

      <main className="legal-content">
        <div className="legal-eyebrow">Documento Legal · LGPD</div>
        <h1 className="legal-title">Política de Privacidade</h1>
        <p className="legal-updated">Última atualização: 26 de abril de 2026</p>

        <p>
          Esta Política de Privacidade descreve como o Arcane Pro coleta, utiliza, armazena e protege seus dados pessoais, em conformidade com a <strong>Lei Geral de Proteção de Dados Pessoais (Lei nº 13.709/2018 — LGPD)</strong>.
        </p>
        <p>Ao utilizar a Plataforma, você concorda com as práticas descritas nesta Política. Se não concordar, por favor não utilize o serviço.</p>

        <h2>1. Quem somos (Controlador dos Dados)</h2>
        <p><strong>Operador / Controlador:</strong></p>
        <ul>
          <li><strong>Nome:</strong> Gabriel Polizel</li>
          <li><strong>Natureza:</strong> Pessoa Física, atuando como operador da Plataforma Arcane Pro</li>
          <li><strong>E-mail de contato:</strong> <a href="mailto:contato@arcanepro.com.br">contato@arcanepro.com.br</a></li>
          <li><strong>Plataforma:</strong> <a href="https://arcanepro.com.br">arcanepro.com.br</a></li>
        </ul>
        <p>Para fins da LGPD, o Operador atua como <strong>Controlador</strong> dos dados pessoais tratados pela Plataforma.</p>

        <h3>1.1. Encarregado de Proteção de Dados (DPO)</h3>
        <p>Por se tratar de pessoa física, o próprio Operador atua como ponto de contato para questões de privacidade, podendo ser contatado por meio do e-mail acima.</p>

        <h2>2. Dados que coletamos</h2>

        <h3>2.1. Dados fornecidos diretamente por você</h3>
        <p><strong>No cadastro:</strong></p>
        <ul>
          <li>E-mail</li>
          <li>Senha (armazenada de forma criptografada)</li>
        </ul>
        <p><strong>No preenchimento de perfil da empresa (opcional):</strong></p>
        <ul>
          <li>Nome da empresa ou nome próprio</li>
          <li>Setor de atuação</li>
          <li>Porte da empresa</li>
          <li>Cidade</li>
          <li>Tom de comunicação preferencial</li>
        </ul>

        <h3>2.2. Dados gerados pelo uso da Plataforma</h3>
        <ul>
          <li>Histórico de análises realizadas (tool utilizada, módulo, data/hora)</li>
          <li>Conteúdo dos textos enviados como contexto</li>
          <li>Resultados gerados pela inteligência artificial</li>
          <li>Quantidade de análises consumidas no período de cobrança</li>
          <li>Plano de assinatura ativo</li>
        </ul>

        <h3>2.3. Conteúdo de arquivos enviados</h3>
        <p>Quando você faz upload de planilhas, contratos ou outros documentos para análise:</p>
        <ul>
          <li>O conteúdo do arquivo é <strong>temporariamente processado</strong> em nosso backend e enviado a provedores de inteligência artificial (Anthropic, modelo Claude)</li>
          <li>O conteúdo bruto <strong>não é armazenado permanentemente</strong> após a análise</li>
          <li>Apenas um resumo do <strong>resultado textual</strong> da análise é salvo no histórico</li>
        </ul>
        <div className="legal-callout">
          <strong>Recomendação importante:</strong> evite enviar à Plataforma documentos contendo dados pessoais sensíveis de terceiros sem ter base legal para tanto.
        </div>

        <h3>2.4. Dados coletados automaticamente</h3>
        <ul>
          <li>Endereço IP (para segurança e prevenção de fraudes)</li>
          <li>Tipo de dispositivo, navegador e sistema operacional</li>
          <li>Logs de acesso e uso da Plataforma</li>
        </ul>

        <h3>2.5. Dados de pagamento</h3>
        <ul>
          <li>O Operador <strong>não coleta nem armazena</strong> dados de cartão de crédito</li>
          <li>Pagamentos são processados pela <strong>Kirvano</strong> (gateway de pagamento), que tem sua própria política de privacidade</li>
          <li>Recebemos da Kirvano apenas: confirmação de pagamento, plano contratado e e-mail do comprador, para ativação da assinatura</li>
        </ul>

        <h2>3. Como usamos seus dados (finalidades)</h2>
        <p>Tratamos seus dados pessoais para:</p>

        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr><th>Finalidade</th><th>Base legal (LGPD)</th></tr>
            </thead>
            <tbody>
              <tr><td>Criar e manter sua conta na Plataforma</td><td>Execução de contrato (art. 7º, V)</td></tr>
              <tr><td>Processar suas análises e gerar resultados</td><td>Execução de contrato</td></tr>
              <tr><td>Personalizar análises com contexto da empresa</td><td>Consentimento do Usuário (art. 7º, I)</td></tr>
              <tr><td>Cobrar mensalidades e gerenciar assinaturas</td><td>Execução de contrato</td></tr>
              <tr><td>Enviar comunicações operacionais</td><td>Legítimo interesse (art. 7º, IX)</td></tr>
              <tr><td>Garantir segurança e prevenir fraudes</td><td>Legítimo interesse</td></tr>
              <tr><td>Melhorar a Plataforma (análise agregada)</td><td>Legítimo interesse, com dados anonimizados</td></tr>
              <tr><td>Cumprir obrigações legais</td><td>Cumprimento de obrigação legal (art. 7º, II)</td></tr>
            </tbody>
          </table>
        </div>

        <h3>3.1. O que NÃO fazemos com seus dados</h3>
        <ul>
          <li>Não vendemos seus dados a terceiros;</li>
          <li>Não usamos o conteúdo dos seus arquivos para treinar modelos de IA;</li>
          <li>Não compartilhamos histórico de análises com nenhum terceiro além dos provedores estritamente necessários para entrega do serviço;</li>
          <li>Não usamos seus dados para anúncios direcionados.</li>
        </ul>

        <h2>4. Com quem compartilhamos seus dados</h2>
        <p>Para entregar o serviço, alguns dados são processados por terceiros que atuam como <strong>operadores</strong> (subprocessadores):</p>

        <h3>4.1. Provedores de infraestrutura</h3>
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr><th>Provedor</th><th>Função</th><th>Dados acessados</th></tr>
            </thead>
            <tbody>
              <tr><td><strong>Supabase</strong> (USA/EU)</td><td>Banco de dados, autenticação</td><td>Conta, perfil, histórico</td></tr>
              <tr><td><strong>Vercel</strong> (USA)</td><td>Hospedagem do frontend</td><td>Dados de acesso (IP, navegador)</td></tr>
              <tr><td><strong>Railway</strong> (USA)</td><td>Hospedagem do backend</td><td>Conteúdo enviado (em trânsito)</td></tr>
              <tr><td><strong>Anthropic</strong> (USA)</td><td>Modelo de IA (Claude)</td><td>Conteúdo dos arquivos para análise</td></tr>
              <tr><td><strong>Kirvano</strong> (Brasil)</td><td>Processamento de pagamentos</td><td>Dados de cobrança e e-mail</td></tr>
            </tbody>
          </table>
        </div>

        <h3>4.2. Transferência internacional</h3>
        <p>Alguns desses operadores estão localizados fora do Brasil. A transferência ocorre conforme o art. 33 da LGPD, com base em garantias contratuais e por ser <strong>necessária para a execução do contrato</strong> com você.</p>

        <h3>4.3. Outras hipóteses</h3>
        <p>Podemos compartilhar dados com terceiros nas seguintes situações:</p>
        <ul>
          <li>Cumprimento de ordem judicial ou determinação legal;</li>
          <li>Investigação de fraudes ou violações destes Termos;</li>
          <li>Em caso de venda, fusão ou cisão da operação (com manutenção do nível de proteção).</li>
        </ul>

        <h2>5. Por quanto tempo guardamos seus dados</h2>
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr><th>Tipo de dado</th><th>Período de retenção</th></tr>
            </thead>
            <tbody>
              <tr><td>Cadastro e perfil</td><td>Enquanto a conta estiver ativa, ou até 5 anos após o cancelamento</td></tr>
              <tr><td>Histórico de análises</td><td>12 meses por padrão; planos pagos podem ter retenção maior</td></tr>
              <tr><td>Conteúdo bruto de arquivos enviados</td><td>Não armazenamos permanentemente — apagado após processamento</td></tr>
              <tr><td>Dados de pagamento</td><td>5 anos, conforme exigência fiscal</td></tr>
              <tr><td>Logs de segurança</td><td>Até 6 meses</td></tr>
            </tbody>
          </table>
        </div>
        <p>Após o término dos prazos, os dados são <strong>eliminados ou anonimizados</strong>, exceto quando a lei exigir retenção mais longa.</p>

        <h2>6. Seus direitos como titular dos dados</h2>
        <p>Conforme o art. 18 da LGPD, você tem direito a:</p>
        <ol>
          <li><strong>Confirmação</strong> de que tratamos seus dados;</li>
          <li><strong>Acesso</strong> aos dados que possuímos sobre você;</li>
          <li><strong>Correção</strong> de dados incompletos, inexatos ou desatualizados;</li>
          <li><strong>Anonimização, bloqueio ou eliminação</strong> de dados desnecessários ou tratados em desconformidade;</li>
          <li><strong>Portabilidade</strong> dos dados a outro fornecedor;</li>
          <li><strong>Eliminação</strong> dos dados tratados com base em consentimento;</li>
          <li><strong>Informação</strong> sobre com quem compartilhamos seus dados;</li>
          <li><strong>Revogação do consentimento</strong>, quando aplicável.</li>
        </ol>

        <h3>6.1. Como exercer seus direitos</h3>
        <p>Envie um e-mail para <a href="mailto:contato@arcanepro.com.br">contato@arcanepro.com.br</a> com:</p>
        <ul>
          <li>Assunto: "LGPD — [tipo do pedido]"</li>
          <li>Descrição clara do pedido</li>
          <li>Comprovação de identidade (e-mail cadastrado deve coincidir)</li>
        </ul>
        <p><strong>Prazo de resposta:</strong> até <strong>15 dias</strong> a contar do recebimento.</p>

        <h3>6.2. Limitações</h3>
        <p>Alguns pedidos podem ser parcialmente atendidos quando há obrigação legal ou contratual de manter os dados (ex: dados fiscais).</p>

        <h2>7. Segurança dos dados</h2>
        <p>Adotamos medidas técnicas e organizacionais para proteger seus dados:</p>
        <ul>
          <li>Senhas armazenadas de forma criptografada (hash);</li>
          <li>Comunicação com a Plataforma criptografada via HTTPS/TLS;</li>
          <li>Acesso aos dados restrito por <strong>Row Level Security</strong> no banco de dados;</li>
          <li>Acesso ao sistema de produção restrito ao Operador;</li>
          <li>Backups periódicos do banco de dados.</li>
        </ul>

        <h3>7.1. Notificação de incidentes</h3>
        <p>Em caso de incidente de segurança que possa causar <strong>risco ou dano relevante</strong> aos titulares, comunicaremos:</p>
        <ul>
          <li>A <strong>Autoridade Nacional de Proteção de Dados (ANPD)</strong>, conforme art. 48 da LGPD;</li>
          <li>Os <strong>titulares afetados</strong>, por e-mail, em até 72 horas após a constatação do incidente.</li>
        </ul>

        <h2>8. Cookies e tecnologias similares</h2>
        <p>A Plataforma utiliza cookies e tecnologias similares para:</p>
        <ul>
          <li>Manter sessão autenticada (cookies essenciais);</li>
          <li>Lembrar preferências do Usuário;</li>
          <li>Analisar uso da Plataforma de forma agregada.</li>
        </ul>
        <p>Você pode desabilitar cookies no seu navegador, porém isso pode impactar funcionalidades da Plataforma.</p>

        <h2>9. Crianças e adolescentes</h2>
        <p>A Plataforma <strong>não é destinada a menores de 18 anos</strong>. Não coletamos intencionalmente dados de menores. Se você é responsável por um menor que tenha enviado dados, entre em contato pelo e-mail acima para que possamos eliminar essas informações.</p>

        <h2>10. Alterações nesta Política</h2>
        <p>Podemos atualizar esta Política periodicamente. Quando houver mudança <strong>substancial</strong>:</p>
        <ul>
          <li>Comunicaremos com <strong>30 dias de antecedência</strong> por e-mail;</li>
          <li>A nova versão indicará a data de atualização no topo do documento;</li>
          <li>O uso continuado após a vigência implica aceitação tácita.</li>
        </ul>

        <h2>11. Sucessão (caso o Operador constitua pessoa jurídica)</h2>
        <p>Caso o Arcane Pro venha a ser operado por pessoa jurídica controlada pelo mesmo Operador, esta Política permanece válida com a sucessão automática, mantendo-se ou aprimorando-se o nível de proteção dos dados, sendo o Usuário informado por e-mail.</p>

        <h2>12. Lei aplicável e contato com a ANPD</h2>
        <p>Esta Política é regida pela legislação brasileira, em especial pela <strong>Lei Geral de Proteção de Dados Pessoais (Lei nº 13.709/2018)</strong>.</p>

        <h3>12.1. Reclamações</h3>
        <p>Caso entenda que seus direitos não foram adequadamente atendidos, você pode apresentar reclamação à:</p>
        <p>
          <strong>Autoridade Nacional de Proteção de Dados (ANPD)</strong><br />
          <a href="https://www.gov.br/anpd" target="_blank" rel="noopener noreferrer">gov.br/anpd</a>
        </p>

        <h2>13. Contato</h2>
        <p>Para qualquer dúvida sobre esta Política de Privacidade ou sobre o tratamento dos seus dados pessoais:</p>
        <p>
          <strong>E-mail:</strong> <a href="mailto:contato@arcanepro.com.br">contato@arcanepro.com.br</a><br />
          <strong>Site:</strong> <a href="https://arcanepro.com.br">arcanepro.com.br</a>
        </p>

        <LegalFooter />
      </main>
    </div>
  );
}